/**
 * Canonical per-entity party / resource action catalog (labels + descriptions).
 * Keys are stable snakeCase verbs stored in PartyTemplate.roles[].permittedActions.
 */

/** @typedef {{ tr: string, en: string }} LocalizedString */

/** @typedef {{ label: LocalizedString, description: LocalizedString, category?: string }} EntityActionMeta */

/** @type {Record<string, Record<string, EntityActionMeta>>} */
export const ENTITY_ACTION_REGISTRY = Object.freeze({
    Document: Object.freeze({
        view: {
            category: 'read',
            label: { tr: 'Görüntüle', en: 'View' },
            description: {
                tr: 'Dokümanı ve revizyonlarını okuyabilir; indirme ve önizleme yapabilir.',
                en: 'Read the document and revisions; preview and download.',
            },
        },
        edit: {
            category: 'write',
            label: { tr: 'Düzenle', en: 'Edit' },
            description: {
                tr: 'Başlık, sınıflandırma, birim ve ilgili alanları güncelleyebilir.',
                en: 'Update metadata such as title, classification, unit, and related fields.',
            },
        },
        revise: {
            category: 'write',
            label: { tr: 'Revize et', en: 'Revise' },
            description: {
                tr: 'İçerik ve revizyon üzerinde değişiklik yapabilir (metin / yapı).',
                en: 'Change document content and create or update revisions.',
            },
        },
        designReport: {
            category: 'write',
            label: { tr: 'Rapor tasarımı', en: 'Report design' },
            description: {
                tr: 'Dinamik rapor tasarımcısı ve şablon bağlama işlemlerini yapabilir.',
                en: 'Use the dynamic report designer and template binding features.',
            },
        },
        delete: {
            category: 'destructive',
            label: { tr: 'Sil', en: 'Delete' },
            description: {
                tr: 'Dokümanı ve ilişkili verileri silebilir.',
                en: 'Delete the document and related data.',
            },
        },
        export: {
            category: 'read',
            label: { tr: 'Dışa aktar', en: 'Export' },
            description: {
                tr: 'PDF veya diğer dışa aktarma formatlarında çıktı alabilir.',
                en: 'Export to PDF or other supported formats.',
            },
        },
        approve: {
            category: 'workflow',
            label: { tr: 'Onayla', en: 'Approve' },
            description: {
                tr: 'Onay akışında onaylayıcı olarak hareket edebilir.',
                en: 'Act as an approver in approval flows.',
            },
        },
        reject: {
            category: 'workflow',
            label: { tr: 'Reddet', en: 'Reject' },
            description: {
                tr: 'Onay akışında reddedebilir.',
                en: 'Reject in approval flows.',
            },
        },
        comment: {
            category: 'collab',
            label: { tr: 'Yorumla', en: 'Comment' },
            description: {
                tr: 'Yorum ve geri bildirim ekleyebilir.',
                en: 'Add comments and feedback.',
            },
        },
        sign: {
            category: 'workflow',
            label: { tr: 'İmzala', en: 'Sign' },
            description: {
                tr: 'Elektronik veya onay imzası gerektiren adımlarda imzalayabilir.',
                en: 'Sign where electronic or approval signatures are required.',
            },
        },
    }),
    Asset: Object.freeze({
        view: {
            category: 'read',
            label: { tr: 'Görüntüle', en: 'View' },
            description: {
                tr: 'Varlık kartını ve geçmişini okuyabilir.',
                en: 'Read the asset record and history.',
            },
        },
        edit: {
            category: 'write',
            label: { tr: 'Düzenle', en: 'Edit' },
            description: {
                tr: 'Varlık alanlarını güncelleyebilir.',
                en: 'Update asset fields.',
            },
        },
        delete: {
            category: 'destructive',
            label: { tr: 'Sil', en: 'Delete' },
            description: {
                tr: 'Varlığı silebilir veya arşivleyebilir.',
                en: 'Delete or archive the asset.',
            },
        },
        export: {
            category: 'read',
            label: { tr: 'Dışa aktar', en: 'Export' },
            description: {
                tr: 'Varlık verisini dışa aktarabilir.',
                en: 'Export asset data.',
            },
        },
        transfer: {
            category: 'write',
            label: { tr: 'Transfer', en: 'Transfer' },
            description: {
                tr: 'Sahiplik veya birim transferi yapabilir.',
                en: 'Transfer ownership or organizational placement.',
            },
        },
        decommission: {
            category: 'write',
            label: { tr: 'Devreden çıkar', en: 'Decommission' },
            description: {
                tr: 'Varlığı kullanımdan kaldırma / hurda sürecini yürütebilir.',
                en: 'Run decommission or disposal workflows.',
            },
        },
        approve: {
            category: 'workflow',
            label: { tr: 'Onayla', en: 'Approve' },
            description: {
                tr: 'Yaşam döngüsü veya değişiklik onaylarında onaylayabilir.',
                en: 'Approve lifecycle or change steps.',
            },
        },
        comment: {
            category: 'collab',
            label: { tr: 'Yorumla', en: 'Comment' },
            description: {
                tr: 'Yorum ekleyebilir.',
                en: 'Add comments.',
            },
        },
    }),
    Risk: Object.freeze({
        view: {
            category: 'read',
            label: { tr: 'Görüntüle', en: 'View' },
            description: {
                tr: 'Risk kaydını okuyabilir.',
                en: 'Read the risk record.',
            },
        },
        edit: {
            category: 'write',
            label: { tr: 'Düzenle', en: 'Edit' },
            description: {
                tr: 'Risk alanlarını güncelleyebilir.',
                en: 'Update risk fields.',
            },
        },
        assess: {
            category: 'write',
            label: { tr: 'Değerlendir', en: 'Assess' },
            description: {
                tr: 'Analiz ve değerlendirme adımlarını yürütebilir.',
                en: 'Perform analysis and assessment steps.',
            },
        },
        accept: {
            category: 'workflow',
            label: { tr: 'Kabul / Kapat', en: 'Accept / Close' },
            description: {
                tr: 'Risk kabulü veya kapatma işlemlerini yapabilir.',
                en: 'Accept residual risk or close the risk.',
            },
        },
        transfer: {
            category: 'write',
            label: { tr: 'Transfer', en: 'Transfer' },
            description: {
                tr: 'Risk sahipliğini veya sorumluluğunu devredebilir.',
                en: 'Transfer risk ownership or accountability.',
            },
        },
        delete: {
            category: 'destructive',
            label: { tr: 'Sil', en: 'Delete' },
            description: {
                tr: 'Risk kaydını silebilir.',
                en: 'Delete the risk record.',
            },
        },
        export: {
            category: 'read',
            label: { tr: 'Dışa aktar', en: 'Export' },
            description: {
                tr: 'Risk verisini dışa aktarabilir.',
                en: 'Export risk data.',
            },
        },
        approve: {
            category: 'workflow',
            label: { tr: 'Onayla', en: 'Approve' },
            description: {
                tr: 'İşlem veya tedavi onaylarında onaylayabilir.',
                en: 'Approve treatment or workflow steps.',
            },
        },
        comment: {
            category: 'collab',
            label: { tr: 'Yorumla', en: 'Comment' },
            description: {
                tr: 'Yorum ekleyebilir.',
                en: 'Add comments.',
            },
        },
    }),
    ServiceCatalogItem: Object.freeze({
        view: {
            category: 'read',
            label: { tr: 'Görüntüle', en: 'View' },
            description: {
                tr: 'Hizmet kataloğu kaydını okuyabilir.',
                en: 'Read the service catalog item.',
            },
        },
        edit: {
            category: 'write',
            label: { tr: 'Düzenle', en: 'Edit' },
            description: {
                tr: 'Hizmet kataloğu alanlarını güncelleyebilir.',
                en: 'Update service catalog fields.',
            },
        },
        comment: {
            category: 'collab',
            label: { tr: 'Yorumla', en: 'Comment' },
            description: {
                tr: 'Yorum ekleyebilir.',
                en: 'Add comments.',
            },
        },
        export: {
            category: 'read',
            label: { tr: 'Dışa aktar', en: 'Export' },
            description: {
                tr: 'Hizmet kataloğu verisini dışa aktarabilir.',
                en: 'Export service catalog data.',
            },
        },
        delete: {
            category: 'destructive',
            label: { tr: 'Sil', en: 'Delete' },
            description: {
                tr: 'Hizmet kataloğu kaydını silebilir.',
                en: 'Delete the service catalog item.',
            },
        },
        approve: {
            category: 'workflow',
            label: { tr: 'Onayla', en: 'Approve' },
            description: {
                tr: 'Hizmet kataloğu onay adımlarında onaylayabilir.',
                en: 'Approve service catalog workflow steps.',
            },
        },
    }),
});

const ENTITY_TYPE_ALIASES = {
    document: 'Document',
    asset: 'Asset',
    risk: 'Risk',
    riskregister: 'Risk',
    correctiveaction: 'Action',
    action: 'Action',
    servicecatalogitem: 'ServiceCatalogItem',
};

/**
 * @param {string} entityType
 * @returns {string}
 */
export function normalizeEntityTypeForActions(entityType) {
    const raw = String(entityType || '').trim();
    if (!raw) return '';
    const lower = raw.toLowerCase();
    if (ENTITY_TYPE_ALIASES[lower]) return ENTITY_TYPE_ALIASES[lower];
    return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
}

/**
 * @param {string} entityType
 * @returns {Record<string, EntityActionMeta> | null}
 */
export function getEntityActionCatalog(entityType) {
    const key = normalizeEntityTypeForActions(entityType);
    return ENTITY_ACTION_REGISTRY[key] || null;
}

/**
 * Resolve a stored or user-entered action to the canonical registry key.
 * @param {string} entityType
 * @param {string} action
 * @returns {string | null}
 */
export function resolveEntityActionKey(entityType, action) {
    const cat = getEntityActionCatalog(entityType);
    if (!cat || action == null) return null;
    const raw = String(action).trim();
    if (!raw) return null;
    if (raw.toLowerCase() === 'read') {
        return Object.prototype.hasOwnProperty.call(cat, 'view') ? 'view' : null;
    }
    if (Object.prototype.hasOwnProperty.call(cat, raw)) return raw;
    const lower = raw.toLowerCase();
    const found = Object.keys(cat).find((key) => key.toLowerCase() === lower);
    return found || null;
}

/**
 * @param {string} entityType
 * @param {string} action
 * @returns {boolean}
 */
export function isValidEntityAction(entityType, action) {
    return resolveEntityActionKey(entityType, action) != null;
}

/**
 * @returns {string[]}
 */
export function listEntityTypesWithActionCatalog() {
    return Object.keys(ENTITY_ACTION_REGISTRY);
}

// ── My Work / ChangeDiffView: entity → reference hydration profile ───────────

/** @typedef {'asset'|'document'|'action'|'risk'|'generic'} EntityDiffReferenceProfile */

/** @type {Record<string, EntityDiffReferenceProfile>} */
const ENTITY_DIFF_REFERENCE_PROFILE = Object.freeze({
    Asset: 'asset',
    Document: 'document',
    Action: 'action',
    CorrectiveAction: 'action',
    Risk: 'risk',
    RiskRegister: 'risk',
});

/** @type {Record<string, Record<string, string>>} */
const RISK_DIFF_FIELD_LABEL_KEYS = Object.freeze({
    riskId: 'fields.riskId',
    riskNature: 'fields.riskNature',
    responseStrategy: 'fields.responseStrategy',
    responseDescription: 'fields.responseDescription',
    riskLevel: 'fields.riskLevel',
    score: 'fields.score',
    residualScore: 'fields.residualScore',
    causes: 'fields.causes',
    consequences: 'fields.consequences',
    existingControls: 'fields.existingControls',
    relatedAssetCategories: 'fields.relatedAssetCategories',
    relatedProcesses: 'fields.relatedProcesses',
    treatments: 'fields.treatments',
    partyInstanceId: 'fields.partyInstanceId',
});

const ASSET_DIFF_FIELD_LABEL_KEYS = Object.freeze({
    relatedThreats: 'fields.relatedThreats',
    relatedControls: 'fields.relatedControls',
    linkVulnerabilities: 'fields.linkVulnerabilities',
    linkProcesses: 'fields.linkProcesses',
    linkVendors: 'fields.linkVendors',
    linkPdInventory: 'fields.linkPdInventory',
    linkLicenses: 'fields.linkLicenses',
    linkDisposals: 'fields.linkDisposals',
});

const INCIDENT_DIFF_FIELD_LABEL_KEYS = Object.freeze({
    code: 'fields.code',
    severity: 'fields.severity',
    type: 'fields.type',
    impact: 'fields.impact',
    urgency: 'fields.urgency',
    status: 'fields.status',
    workflowTemplateId: 'fields.workflowTemplateId',
});

/** @type {Record<string, Record<string, string>>} */
const ENTITY_DIFF_EXTRA_FIELD_LABEL_KEYS = Object.freeze({
    Asset: ASSET_DIFF_FIELD_LABEL_KEYS,
    Risk: RISK_DIFF_FIELD_LABEL_KEYS,
    Incident: INCIDENT_DIFF_FIELD_LABEL_KEYS,
});

/** Shared field keys for approval diff (matches myWork.detail.fields.* in web). */
export const ENTITY_DIFF_BASE_FIELD_LABEL_KEYS = Object.freeze({
    assetName: 'fields.assetName',
    assetId: 'fields.assetId',
    category: 'fields.category',
    assetGroup: 'fields.assetGroup',
    location: 'fields.location',
    unit: 'fields.unit',
    owner: 'fields.owner',
    custodian: 'fields.custodian',
    partyAssignments: 'fields.partyAssignments',
    usingStatus: 'fields.usingStatus',
    dependentBeing: 'fields.dependentBeing',
    confidentialityValue: 'fields.confidentialityValue',
    integrityValue: 'fields.integrityValue',
    availabilityValue: 'fields.availabilityValue',
    classificationLevel: 'fields.classificationLevel',
    amount: 'fields.amount',
    definition: 'fields.definition',
    detail: 'fields.detail',
    relationships: 'fields.relationships',
    relatedRisks: 'fields.relatedRisks',
    relatedThreats: 'fields.relatedThreats',
    relatedControls: 'fields.relatedControls',
    linkVulnerabilities: 'fields.linkVulnerabilities',
    linkProcesses: 'fields.linkProcesses',
    linkVendors: 'fields.linkVendors',
    linkPdInventory: 'fields.linkPdInventory',
    linkLicenses: 'fields.linkLicenses',
    linkDisposals: 'fields.linkDisposals',
    title: 'fields.title',
    description: 'fields.description',
    actionType: 'fields.actionType',
    strategy: 'fields.strategy',
    plan: 'fields.plan',
    rootCause: 'fields.rootCause',
    rootCauseReasons: 'fields.rootCauseReasons',
    preventiveRationale: 'fields.preventiveRationale',
    improvementObjective: 'fields.improvementObjective',
    startDate: 'fields.startDate',
    deadline: 'fields.deadline',
    budget: 'fields.budget',
    budgetStatus: 'fields.budgetStatus',
    status: 'fields.status',
    progress: 'fields.progress',
    sourceType: 'fields.sourceType',
    sourceId: 'fields.sourceId',
    sourceTitle: 'fields.sourceTitle',
    rootCauseAnalysis: 'fields.rootCauseAnalysis',
    rootCauseMethod: 'fields.rootCauseMethod',
    effectivenessVerification: 'fields.effectivenessVerification',
    verifiedBy: 'fields.verifiedBy',
    verifiedDate: 'fields.verifiedDate',
    pdcaStatus: 'fields.pdcaStatus',
    assignmentTemplate: 'fields.assignmentTemplate',
    documentType: 'fields.documentType',
    classification: 'fields.classification',
    relatedVendors: 'fields.relatedVendors',
    workflowTemplateId: 'fields.workflowTemplateId',
    slaRef: 'fields.slaRef',
    assetCategory: 'fields.assetCategory',
    impact: 'fields.impact',
    probability: 'fields.probability',
    priority: 'fields.priority',
    financialImpact: 'fields.financialImpact',
    annualizedLoss: 'fields.annualizedLoss',
});

export const ENTITY_DIFF_FIELD_VISIBILITY = Object.freeze({
    PRIMARY: 'primary',
    TECHNICAL: 'technical',
});

export const ENTITY_DIFF_FIELD_GROUPS = Object.freeze({
    IDENTITY: 'identity',
    OWNERSHIP: 'ownership',
    CLASSIFICATION: 'classification',
    RELATIONSHIPS: 'relationships',
    FINANCIAL: 'financial',
    LIFECYCLE: 'lifecycle',
    CONTENT: 'content',
    INCIDENT: 'incident',
    RISK: 'risk',
    ACTION: 'action',
    TECHNICAL: 'technical',
    OTHER: 'other',
});

const GLOBAL_TECHNICAL_DIFF_FIELDS = new Set([
    '_id',
    'id',
    'tenant',
    'tenantId',
    'createdBy',
    'updatedBy',
    'createdAt',
    'updatedAt',
    '__v',
    'workflowTemplateId',
    'workflowInstanceId',
    'workflowApprovalStatus',
    'approvalStatus',
    'currentWorkflowStep',
    'workflowStepTotal',
    'myEffectiveActions',
    'effectiveActions',
    'pageKey',
    'triggerEvent',
]);

function createDiffFieldMeta(group, order, visibility = ENTITY_DIFF_FIELD_VISIBILITY.PRIMARY, reason = null) {
    return { visibility, group, order, reason };
}

function technicalDiffFieldMeta(order, reason) {
    return createDiffFieldMeta(
        ENTITY_DIFF_FIELD_GROUPS.TECHNICAL,
        order,
        ENTITY_DIFF_FIELD_VISIBILITY.TECHNICAL,
        reason,
    );
}

function createEntityDiffFieldMeta(entries) {
    return Object.freeze(Object.fromEntries(entries));
}

const ENTITY_DIFF_FIELD_META = Object.freeze({
    Asset: createEntityDiffFieldMeta([
        ['assetName', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.IDENTITY, 10)],
        ['assetId', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.IDENTITY, 20)],
        ['category', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.IDENTITY, 30)],
        ['assetCategory', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.IDENTITY, 40)],
        ['assetGroup', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.OWNERSHIP, 50)],
        ['location', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.OWNERSHIP, 60)],
        ['unit', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.OWNERSHIP, 70)],
        ['owner', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.OWNERSHIP, 80)],
        ['custodian', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.OWNERSHIP, 90)],
        ['partyAssignments', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.OWNERSHIP, 100)],
        ['usingStatus', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.CLASSIFICATION, 110)],
        ['dependentBeing', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.CLASSIFICATION, 120)],
        ['confidentialityValue', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.CLASSIFICATION, 130)],
        ['integrityValue', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.CLASSIFICATION, 140)],
        ['availabilityValue', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.CLASSIFICATION, 150)],
        ['classificationLevel', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.CLASSIFICATION, 160)],
        ['containsPersonalData', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.CLASSIFICATION, 170)],
        ['amount', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.FINANCIAL, 180)],
        ['financialValue', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.FINANCIAL, 190)],
        ['assetValue', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.FINANCIAL, 200)],
        ['cost', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.FINANCIAL, 210)],
        ['price', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.FINANCIAL, 220)],
        ['purchaseValue', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.FINANCIAL, 230)],
        ['replacementValue', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.FINANCIAL, 240)],
        ['currentValue', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.FINANCIAL, 250)],
        ['purchasePrice', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.FINANCIAL, 260)],
        ['currency', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.FINANCIAL, 270)],
        ['definition', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.CONTENT, 290)],
        ['detail', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.CONTENT, 300)],
        ['reviewFrequency', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.LIFECYCLE, 310)],
        ['nextReviewAt', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.LIFECYCLE, 315)],
        ['lastReviewedAt', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.LIFECYCLE, 316)],
        ['expectedLifespan', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.LIFECYCLE, 320)],
        ['version', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.LIFECYCLE, 330)],
        ['workflowTemplateId', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.LIFECYCLE, 340)],
        ['slaRef', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.LIFECYCLE, 350)],
        ['relationships', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.RELATIONSHIPS, 360)],
        ['processes', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.RELATIONSHIPS, 370)],
        ['relatedProcesses', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.RELATIONSHIPS, 380)],
        ['relatedRisks', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.RELATIONSHIPS, 390)],
        ['relatedThreats', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.RELATIONSHIPS, 400)],
        ['relatedControls', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.RELATIONSHIPS, 410)],
        ['linkVulnerabilities', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.RELATIONSHIPS, 420)],
        ['linkProcesses', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.RELATIONSHIPS, 430)],
        ['linkVendors', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.RELATIONSHIPS, 440)],
        ['linkPdInventory', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.RELATIONSHIPS, 450)],
        ['linkLicenses', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.RELATIONSHIPS, 460)],
        ['linkDisposals', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.RELATIONSHIPS, 470)],
        ['assetCategoryDisplay', technicalDiffFieldMeta(480, 'computed')],
        ['securityValue', technicalDiffFieldMeta(481, 'computed')],
        ['linkedOperationalControlCount', technicalDiffFieldMeta(482, 'computed')],
        ['linkedVendorCount', technicalDiffFieldMeta(483, 'computed')],
        ['linkedPersonalDataInventoryCount', technicalDiffFieldMeta(484, 'computed')],
        ['linkedLicenseRecordCount', technicalDiffFieldMeta(485, 'computed')],
        ['linkedDisposalRecordCount', technicalDiffFieldMeta(486, 'computed')],
        ['linkedLifecycleEventCount', technicalDiffFieldMeta(487, 'computed')],
        ['relationCategory', technicalDiffFieldMeta(490, 'default-noise')],
        ['relatedRiskLinks', technicalDiffFieldMeta(500, 'default-noise')],
    ]),
    Incident: createEntityDiffFieldMeta([
        ['code', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.IDENTITY, 10)],
        ['title', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.IDENTITY, 20)],
        ['description', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.CONTENT, 30)],
        ['severity', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.INCIDENT, 40)],
        ['type', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.INCIDENT, 50)],
        ['impact', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.INCIDENT, 60)],
        ['urgency', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.INCIDENT, 70)],
        ['status', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.INCIDENT, 80)],
    ]),
    Risk: createEntityDiffFieldMeta([
        ['riskId', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.RISK, 10)],
        ['title', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.RISK, 20)],
        ['description', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.RISK, 30)],
        ['riskNature', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.RISK, 40)],
        ['impact', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.RISK, 50)],
        ['probability', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.RISK, 60)],
        ['priority', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.RISK, 70)],
        ['riskLevel', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.RISK, 80)],
        ['score', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.RISK, 90)],
        ['residualScore', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.RISK, 100)],
        ['financialImpact', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.RISK, 110)],
        ['annualizedLoss', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.RISK, 120)],
        ['causes', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.RELATIONSHIPS, 130)],
        ['consequences', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.RELATIONSHIPS, 140)],
        ['existingControls', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.RELATIONSHIPS, 150)],
        ['relatedAssetCategories', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.RELATIONSHIPS, 160)],
        ['relatedProcesses', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.RELATIONSHIPS, 170)],
        ['treatments', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.RELATIONSHIPS, 180)],
        ['responseStrategy', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.RISK, 190)],
        ['responseDescription', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.RISK, 200)],
        ['partyInstanceId', technicalDiffFieldMeta(210, 'system')],
    ]),
    Action: createEntityDiffFieldMeta([
        ['title', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.ACTION, 10)],
        ['description', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.ACTION, 20)],
        ['actionType', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.ACTION, 30)],
        ['strategy', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.ACTION, 40)],
        ['plan', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.ACTION, 50)],
        ['rootCause', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.ACTION, 60)],
        ['rootCauseReasons', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.ACTION, 70)],
        ['preventiveRationale', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.ACTION, 80)],
        ['improvementObjective', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.ACTION, 90)],
        ['startDate', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.LIFECYCLE, 100)],
        ['deadline', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.LIFECYCLE, 110)],
        ['budget', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.LIFECYCLE, 120)],
        ['budgetStatus', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.LIFECYCLE, 130)],
        ['status', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.LIFECYCLE, 140)],
        ['progress', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.LIFECYCLE, 150)],
        ['sourceType', technicalDiffFieldMeta(151, 'computed')],
        ['sourceId', technicalDiffFieldMeta(152, 'computed')],
        ['sourceTitle', technicalDiffFieldMeta(153, 'computed')],
        ['rootCauseAnalysis', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.ACTION, 160)],
        ['rootCauseMethod', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.ACTION, 170)],
        ['effectivenessVerification', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.ACTION, 180)],
        ['verifiedBy', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.ACTION, 190)],
        ['verifiedDate', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.ACTION, 200)],
        ['pdcaStatus', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.ACTION, 210)],
    ]),
    Document: createEntityDiffFieldMeta([
        ['title', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.CONTENT, 10)],
        ['documentName', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.CONTENT, 20)],
        ['documentCode', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.CONTENT, 30)],
        ['description', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.CONTENT, 40)],
        ['documentType', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.CONTENT, 50)],
        ['classification', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.CONTENT, 60)],
        ['relatedVendors', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.RELATIONSHIPS, 70)],
        ['assignmentTemplate', technicalDiffFieldMeta(75, 'workflow')],
        ['contentMeta', createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.CONTENT, 80)],
    ]),
});

/**
 * @param {string} entityType
 * @param {string} fieldKey
 * @returns {{ visibility: string, group: string, order: number, reason: string|null }}
 */
export function getEntityDiffFieldMeta(entityType, fieldKey) {
    const key = normalizeEntityTypeForActions(entityType);
    const normalizedField = String(fieldKey || '');
    const explicit = ENTITY_DIFF_FIELD_META[key]?.[normalizedField];
    if (explicit) return explicit;
    if (GLOBAL_TECHNICAL_DIFF_FIELDS.has(normalizedField)) {
        const reason = normalizedField.startsWith('workflow') ? 'workflow' : 'system';
        return technicalDiffFieldMeta(900, reason);
    }
    return createDiffFieldMeta(ENTITY_DIFF_FIELD_GROUPS.OTHER, 1000);
}

/**
 * @param {string} entityType
 * @param {string} fieldKey
 * @returns {boolean}
 */
export function isTechnicalEntityDiffField(entityType, fieldKey) {
    return getEntityDiffFieldMeta(entityType, fieldKey).visibility === ENTITY_DIFF_FIELD_VISIBILITY.TECHNICAL;
}

/**
 * @param {string} entityType
 * @returns {EntityDiffReferenceProfile}
 */
export function getEntityDiffReferenceProfile(entityType) {
    const key = normalizeEntityTypeForActions(entityType);
    return ENTITY_DIFF_REFERENCE_PROFILE[key] || 'generic';
}

/**
 * i18n relative key under myWork (e.g. returns `fields.title` → t('fields.title')).
 * @param {string} entityType
 * @param {string} fieldKey
 * @returns {string|null}
 */
export function getDiffFieldLabelKey(entityType, fieldKey) {
    const key = normalizeEntityTypeForActions(entityType);
    const extra = ENTITY_DIFF_EXTRA_FIELD_LABEL_KEYS[key]?.[fieldKey];
    if (extra) return extra;
    const base = ENTITY_DIFF_BASE_FIELD_LABEL_KEYS[fieldKey];
    return base || null;
}

function isLikelyObjectIdString(val) {
    if (val == null || typeof val !== 'string') return false;
    return /^[a-fA-F0-9]{24}$/.test(val.trim());
}

function pushUnitIdForRiskScan(set, val) {
    if (val == null) return;
    if (typeof val === 'object') {
        if (val.name || val.unitName) return;
        const id = val._id || val.id;
        if (id && isLikelyObjectIdString(String(id))) set.add(String(id));
        return;
    }
    if (isLikelyObjectIdString(String(val))) set.add(String(val));
}

function pushOidArray(set, val) {
    if (!Array.isArray(val)) return;
    val.forEach((x) => {
        const id = typeof x === 'object' && x ? x._id || x.id : x;
        if (id && isLikelyObjectIdString(String(id))) set.add(String(id));
    });
}

/**
 * Collect ObjectIds from Risk / RiskRegister payloads for label hydration in ChangeDiffView.
 * @param {object|null|undefined} payload
 * @param {object|null|undefined} previousData
 * @returns {{ unitIds: string[], rootCauseIds: string[], processIds: string[], assetCategoryIds: string[], actionIds: string[] }}
 */
export function collectRiskRegisterReferenceIds(payload, previousData) {
    const unitIds = new Set();
    const rootCauseIds = new Set();
    const processIds = new Set();
    const assetCategoryIds = new Set();
    const actionIds = new Set();

    const scan = (obj) => {
        if (!obj || typeof obj !== 'object') return;
        pushUnitIdForRiskScan(unitIds, obj.unit);
        pushOidArray(rootCauseIds, obj.causes);
        pushOidArray(processIds, obj.relatedProcesses);
        pushOidArray(assetCategoryIds, obj.relatedAssetCategories);
        pushOidArray(actionIds, obj.treatments);
    };

    scan(payload);
    scan(previousData);

    return {
        unitIds: [...unitIds],
        rootCauseIds: [...rootCauseIds],
        processIds: [...processIds],
        assetCategoryIds: [...assetCategoryIds],
        actionIds: [...actionIds],
    };
}
